import { Link } from "react-router-dom";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/context/locale-context";
import { useCart } from "@/context/cart-context";
import { ShoppingBag, Check } from "lucide-react";

export function PaintingCard({ painting }) {
  const { t, formatPrice } = useLocale();
  const { addItem, isInCart } = useCart();
  const inCart = isInCart(painting.id);

  // Toma solo la primera URL si hay varias separadas por ";"
  const firstImage = painting.imageUrl
    ? painting.imageUrl.split(";")[0].trim()
    : null;

  return (
    <div className="group block" data-testid={`painting-card-${painting.id}`}>
      <Link to={`/painting/${painting.id}`} className="block no-underline text-inherit">
        <div className="relative overflow-hidden bg-muted">
          <AspectRatio ratio={4 / 5}>
            {firstImage ? (
              <img
                src={firstImage}
                alt={painting.title}
                className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground bg-muted/50 p-4 text-center">
                <span className="font-serif text-lg opacity-50">{painting.title}</span>
              </div>
            )}
          </AspectRatio>
          <div className="absolute top-4 left-4 flex flex-col gap-2">
            {painting.sold ? (
              <Badge variant="secondary" className="bg-background/80 backdrop-blur text-foreground rounded-none uppercase tracking-widest text-[10px] px-3 py-1">
                {t.card.sold}
              </Badge>
            ) : painting.onSale ? (
              <Badge className="bg-accent text-accent-foreground rounded-none uppercase tracking-widest text-[10px] px-3 py-1">
                {t.card.sale}
              </Badge>
            ) : null}
            {painting.featured && (
              <Badge variant="outline" className="bg-background/80 backdrop-blur text-foreground rounded-none uppercase tracking-widest text-[10px] px-3 py-1 border-none">
                {t.card.featured}
              </Badge>
            )}
          </div>
          {!painting.sold && (
            <div className="absolute bottom-0 left-0 right-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
              <Button
                className="w-full rounded-none h-11 uppercase tracking-widest text-xs font-medium"
                variant={inCart ? "secondary" : "default"}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!inCart) addItem(painting); }}
                data-testid={`add-to-cart-${painting.id}`}
              >
                {inCart
                  ? (<><Check className="w-3.5 h-3.5 mr-2" />{t.cart.inCart}</>)
                  : (<><ShoppingBag className="w-3.5 h-3.5 mr-2" />{t.cart.addToCart}</>)
                }
              </Button>
            </div>
          )}
        </div>
      </Link>
      <div className="mt-4 flex justify-between items-start">
        <Link to={`/painting/${painting.id}`} className="flex-1 min-w-0 no-underline text-inherit">
          <h3 className="font-serif text-lg leading-tight group-hover:text-accent transition-colors truncate">
            {painting.title}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {painting.technique || t.card.mixedMedia}
          </p>
        </Link>
        <div className="text-right ml-4 shrink-0">
          {painting.onSale && painting.salePrice ? (
            <div className="flex flex-col items-end">
              <span className="font-medium">{formatPrice(painting.salePrice)}</span>
              <span className="text-xs text-muted-foreground line-through">{formatPrice(painting.price)}</span>
            </div>
          ) : (
            <span className="font-medium">{formatPrice(painting.price)}</span>
          )}
        </div>
      </div>
    </div>
  );
}